import { PROD, getEnv } from '@/data';
import { isWeb } from '@/utils/constants-platform-specific';

import { getRequestSignature } from '../../screens/Trustly/utils/get-request-signature';
import { TrustlyEstablishData, TrustlyRefreshSplitTokenEstablishData } from '../../types';

// On web the Webview is an iframe: there is no ReactNativeWebView bridge and injectedJavaScript is ignored,
// so route postMessage to the parent window (picked up by Webview.web's listener)
const webBridgeScript = isWeb
    ? `<script>
        window.ReactNativeWebView = {
          postMessage: function (message) {
            window.parent.postMessage(message, '*');
          }
        };
      </script>`
    : '';

// Inlined rather than injectedJavaScript so native and web share one copy (web ignores injectedJavaScript)
// Only forward from Trustly's own frames (same domains trustly.js trusts): on web any frame on the page or an
// opener window can post here, and the bridge would relay it to the app as if it came from this (app-origin) frame
const widgetMessageScript = `window.addEventListener(
          "message",
          function (event) {
            if (
              typeof event.data === "string" &&
              event.data.includes("createTransaction") &&
              /^https:\\/\\/([\\w-]+\\.)*(paywithmybank\\.com|trustly\\.one)$/.test(event.origin)
            ) {
              window.ReactNativeWebView.postMessage(event.data);
            }
          },
          false
        );`;

// JSON.stringify leaves "<" alone, so a value containing "</script>" would end the inline script tag
const toInlineScriptJson = (data: object) => JSON.stringify(data).replace(/</g, '\\u003c');

// On web the iframe must not follow Trustly's returnUrl/cancelUrl redirect (it would load the app nested inside
// itself), so we cancel it and let TrustlyWebview navigate from the forwarded event instead.
// Native keeps the redirect: onShouldStartLoadWithRequest handles it there.
// trustly.js ignores the listener's return value, only obj.preventDefault() cancels the redirect.
const lightboxMessageScript = `Trustly.addPanelListener(function (command, obj) {
            window.ReactNativeWebView.postMessage(command + "|" + JSON.stringify(obj));
            if (${isWeb} && command === "event" && obj && obj.type === "new_location") {
              obj.preventDefault();
            }
          });`;

export const widget = (accessId: string, data: TrustlyEstablishData) => {
    const trustlyBaseUrl = getEnv() === PROD ? 'https://trustly.one' : 'https://sandbox.trustly.one';

    return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta http-equiv="Content-Security-Policy" content="default-src * 'self' data: blob: filesystem: about: ws: wss: 'unsafe-inline' 'unsafe-eval' https://sandbox.trustly.one https://trustly.one; script-src * 'self' data: blob: 'unsafe-inline' 'unsafe-eval' https://sandbox.trustly.one https://trustly.one; style-src * 'self' data: blob: 'unsafe-inline';">  <title>Trustly</title>
      ${webBridgeScript}
      <script src="${trustlyBaseUrl}/start/scripts/trustly.js?accessId=${accessId}" type="text/javascript"></script>
    </head>
    <body>
      <div id="widget"></div>
      <script>
        ${widgetMessageScript}
        var establishData = ${toInlineScriptJson(data)};
        var TrustlyOptions = {
          closeButton: false,
          dragAndDrop: true,
          widgetContainerId: "widget",
        };

        const TrustlyWidgetBankSelected = (data) => {
          return false;
        }

        Trustly.selectBankWidget(establishData, TrustlyOptions, TrustlyWidgetBankSelected);

      </script>
    </body>
    </html>
  `;
};

export const lightbox = async (
    accessId: string,
    data: TrustlyEstablishData | TrustlyRefreshSplitTokenEstablishData
) => {
    if (!data) {
        return;
    }

    const requestSignature = await getRequestSignature(data);
    data.requestSignature = requestSignature || '';

    const trustlyBaseUrl = getEnv() === PROD ? 'https://trustly.one' : 'https://sandbox.trustly.one';

    return `
    <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="Content-Security-Policy" content="default-src * 'self' data: blob: filesystem: about: ws: wss: 'unsafe-inline' 'unsafe-eval' https://sandbox.trustly.one https://trustly.one; script-src * 'self' data: blob: 'unsafe-inline' 'unsafe-eval' https://sandbox.trustly.one https://trustly.one; style-src * 'self' data: blob: 'unsafe-inline';">        <title>Trustly</title>
        ${webBridgeScript}
        <script src="${trustlyBaseUrl}/start/scripts/trustly.js?accessId=${accessId}" type="text/javascript"></script>
      </head>
      <body>
        <div id="widget"></div>
        <script>
          window.onerror = function(msg, url, line) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'error',
              message: msg,
              url: url,
              line: line
            }));
            return true;
          };
          var establishData = ${toInlineScriptJson(data)};
          var TrustlyOptions = {
            closeButton: false,
            dragAndDrop: true,
            widgetContainerId: "widget",
            onError: function(error) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'trustlyError',
                error: error
              }));
            }
          };

          Trustly.establish(establishData, TrustlyOptions);
          ${lightboxMessageScript}

        </script>
      </body>
    </html>
  `;
};
